import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getProspectPublic, prospectLogin, me, getToken } from "@/lib/api";
import { Lock, ArrowRight, AlertCircle, Eye, EyeOff, Mail } from "lucide-react";
import Deck from "@/pages/Deck";
import GiggleProposal from "@/pages/GiggleProposal";
import PortfolioProposal from "@/pages/PortfolioProposal";
import CoreProposal from "@/pages/CoreProposal";
import FlagstarProposal from "@/pages/FlagstarProposal";
import AccountableProposal from "@/pages/AccountableProposal";
import SageProposal from "@/pages/SageProposal";
import RtdProposal from "@/pages/RtdProposal";
import RtdPlanPage from "@/pages/RtdPlanPage";
import ThnbProposal from "@/pages/ThnbProposal";

const CREAM_BG = "linear-gradient(135deg, #FDFBF5 0%, #FBF4DC 100%)";

export default function ProspectGate() {
  const { slug, planId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [code, setCode] = useState("");
  const [gateError, setGateError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [prospect, setProspect] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const data = await getProspectPublic(slug);
        setInfo(data);
        if (getToken()) {
          const session = await me().catch(() => null);
          const slugOk = (session?.prospect_slug || "").toLowerCase() === slug.toLowerCase();
          if (session?.authenticated && (slugOk || session.scope === "master")) {
            setProspect({ ...data, slug: data.slug || slug });
            setAuthed(true);
          }
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await prospectLogin(slug, code.trim());
      // Master code entered on the prospect gate → grant admin access
      // (backend returned scope="master"). Send Colin straight to /admin.
      if (r?.scope === "master") {
        toast.success("Admin access granted");
        nav("/admin");
        return;
      }
      setProspect(r.prospect);
      setAuthed(true);
      toast.success(`Welcome, ${r.prospect.company_name}`);
    } catch {
      setGateError("That access code didn't match. Check the code and try again.");
      toast.error("Invalid access code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-900" style={{ background: CREAM_BG }}>
        <div className="mono text-sm opacity-60">loading…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-ink-900 p-8 grain"
           style={{ background: CREAM_BG }} data-testid="prospect-not-found">
        <AlertCircle className="h-8 w-8 text-sun-700 mb-5" strokeWidth={1.4} />
        <div className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-3">/ 404 · not found</div>
        <h1 className="serif text-4xl md:text-5xl text-center">This pitch link doesn't exist.</h1>
        <p className="mt-5 text-ink-900/65 max-w-md text-center">Double-check the URL, or reach out to Colin at colindarke@complisun.com.</p>
        <button onClick={() => nav("/login")} className="mt-8 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/60 hover:text-sun-700">
          Use master login →
        </button>
      </div>
    );
  }

  if (authed && prospect) {
    const slugLc = (prospect.slug || "").toLowerCase();
    if (slugLc === "giggle-finance") {
      return <GiggleProposal />;
    }
    if (slugLc === "portfolio") {
      return <PortfolioProposal />;
    }
    if (slugLc === "flagstar-bank") {
      return <FlagstarProposal />;
    }
    if (slugLc === "accountable") {
      return <AccountableProposal />;
    }
    if (slugLc === "sage-home-loans") {
      return <SageProposal />;
    }
    if (slugLc === "thnb") {
      return <ThnbProposal />;
    }
    if (slugLc === "rtd-financial") {
      return planId ? <RtdPlanPage planId={planId} /> : <RtdProposal />;
    }
    return <CoreProposal prospect={prospect} />;
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden text-ink-900 grain"
         style={{ background: CREAM_BG }} data-testid="prospect-gate">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full blur-3xl opacity-40"
           style={{ background: "radial-gradient(circle, #F5B400 0%, #FFE070 40%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full blur-3xl opacity-25"
           style={{ background: "radial-gradient(circle, #FF7B1F 0%, transparent 70%)" }} />

      <header className="relative z-10 flex items-center justify-between px-8 md:px-14 py-7">
        <div className="flex items-center gap-3">
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-11 w-11 object-contain" />
          <span className="serif text-xl tracking-tight">CompliSun</span>
          <span className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/50 ml-2">
            Prepared for {info.company_name}
          </span>
        </div>
        <a href="https://complisunguardian.com" target="_blank" rel="noreferrer"
           className="mono text-[11px] uppercase tracking-[0.18em] text-ink-900/55 hover:text-sun-700 transition-colors">
          complisunguardian.com →
        </a>
      </header>

      <main className="relative z-10 px-6 md:px-14 pt-12 md:pt-16 max-w-5xl">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-6 animate-fade-in">
          / private pitch · prepared for
        </p>

        <div className="flex items-center gap-6 mb-8 animate-fade-in-up">
          {info.logo_url && (
            <img src={info.logo_url} alt={info.company_name}
                 className="h-20 md:h-24 max-w-[280px] object-contain bg-white rounded-md p-3 border border-ink-900/8"
                 data-testid="prospect-logo" />
          )}
          <h1 className="serif font-light text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            {info.company_name}
          </h1>
        </div>

        <p className="max-w-2xl text-ink-900/70 text-lg leading-relaxed animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          A private pitch experience built for your team. Enter the access code Colin sent you.
        </p>

        <form onSubmit={onSubmit} className="mt-12 max-w-md animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <label htmlFor="code" className="mono text-[11px] uppercase tracking-[0.22em] text-ink-900/55 flex items-center gap-2 mb-3">
            <Lock className="h-3 w-3" /> Access code
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Input
                id="code"
                data-testid="prospect-code-input"
                type={show ? "text" : "password"}
                autoFocus
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={code}
                onChange={(e) => { setCode(e.target.value); setGateError(""); }}
                aria-invalid={gateError ? "true" : undefined}
                aria-describedby={gateError ? "code-error" : undefined}
                placeholder="access code"
                className="h-14 pr-12 bg-white/70 border-ink-900/15 text-ink-900 placeholder:text-ink-900/35 text-base tracking-[0.2em] focus-visible:ring-sun-500 focus-visible:border-sun-500"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                data-testid="prospect-code-toggle"
                aria-label={show ? "Hide code" : "Show code"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded text-ink-900/55 hover:text-sun-700 hover:bg-sun-500/10 transition-colors"
                tabIndex={-1}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button type="submit" disabled={busy || !code.trim()}
                    data-testid="prospect-code-submit"
                    className="h-14 px-7 rounded-md bg-ink-900 hover:bg-ink-800 text-cream font-medium transition-all disabled:opacity-40">
              {busy ? "Checking…" : <span className="flex items-center gap-2">Enter <ArrowRight className="h-4 w-4" /></span>}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/40">
              Personal link · 6-character code
            </p>
            <a
              href={`mailto:colindarke@complisun.com?subject=${encodeURIComponent(`CompliSun access code request — ${info?.company_name || slug}`)}&body=${encodeURIComponent(`Hi Colin,\n\nI lost my CompliSun access code for ${info?.company_name || slug}. Could you resend it?\n\nThanks`)}`}
              data-testid="prospect-forgot-code-link"
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 hover:text-sun-700 transition-colors"
            >
              <Mail className="h-3 w-3" />
              Forgot the code? Email Colin
            </a>
          </div>
          <p id="code-error" role="alert" aria-live="assertive" data-testid="gate-error"
             className={`text-sm mt-3 ${gateError ? "text-red-800" : "sr-only"}`}>
            {gateError}
          </p>
        </form>
      </main>

      <footer className="relative z-10 mt-24 px-8 md:px-14 py-6 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/40">
        © {new Date().getFullYear()} · CompliSun · Compliance Simplified
      </footer>
    </div>
  );
}
